import React from 'react';

// Local types matching the main file
type DragType = 'scope' | 'section' | 'requirement' | 'sub_requirement' | 'field';
type FieldType = 'text' | 'number' | 'date' | 'boolean' | 'document' | 'calculated' | 'select' | 'multiselect' | 'textarea';

// Local interfaces matching the main file
interface Node {
  id: string;
  type: 'scope' | 'section' | 'requirement' | 'sub_requirement';
  name: string;
  description?: string | undefined;
  children?: Node[];
  fields?: TemplateField[];
  generates_compliance_page?: boolean;
  evidence_required?: boolean;
  evidence_type?: 'document' | 'number' | 'text' | 'mixed';
  assessor_only_notes?: string;
}

interface TemplateField {
  id: number;
  node_id: number;
  field_name: string;
  field_type: string;
  description?: string;
  field_order: number;
  is_required: boolean;
  is_active: boolean;
  validation_rules?: any;
  default_value?: any;
  is_evidence?: boolean;
  allow_multiple?: boolean;
  created_at: string;
  updated_at: string;
}

interface ComplianceStructureBuilderRendererProps {
  // Node data
  nodes: Node[];

  // Template status
  templateStatus: 'draft' | 'published' | 'archived';

  // State
  nodeFields: Map<string, TemplateField[]>;
  selectedNode: Node | null;
  draggedType: DragType | null;
  draggedFieldType: FieldType | null;
  activeTooltip: string | null;
  draggedField: TemplateField | null;
  dragOverFieldId: number | null;
  dragOverPosition: 'before' | 'after' | null;

  // State setters
  setDraggedType: React.Dispatch<React.SetStateAction<DragType | null>>;
  setDraggedFieldType: React.Dispatch<React.SetStateAction<FieldType | null>>;
  setDraggedField: (field: TemplateField | null) => void;
  setDragOverFieldId: (id: number | null) => void;
  setDragOverPosition: (position: 'before' | 'after' | null) => void;

  // Functions
  selectNode: (node: Node) => void;
  deleteNode: (nodeId: string | number) => void;
  handleZoneDrop: (zoneType: string, parentId: string) => (type: DragType) => void;
  handleFieldDrop: (nodeId: string, fieldType: FieldType) => void;
  reorderFields: (dragged: TemplateField, target: TemplateField) => void;
  selectField: (field: TemplateField) => void;
  handleDuplicateNode: (node: Node) => void;
  handleDuplicateField: (field: TemplateField, nodeId: string) => void;
  deleteField: (nodeId: number, fieldId: number) => void;

  // Utilities
  mockToReal: React.MutableRefObject<Map<string, number>>;
}

const ComplianceStructureBuilderRenderer: React.FC<ComplianceStructureBuilderRendererProps> = ({
  nodes,
  templateStatus,
  nodeFields,
  selectedNode,
  draggedType,
  draggedFieldType,
  activeTooltip,
  draggedField,
  dragOverFieldId,
  dragOverPosition,
  setDraggedType,
  setDraggedFieldType,
  setDraggedField,
  setDragOverFieldId,
  setDragOverPosition,
  selectNode,
  deleteNode,
  handleZoneDrop,
  handleFieldDrop,
  reorderFields,
  selectField,
  handleDuplicateNode,
  handleDuplicateField,
  deleteField,
  mockToReal,
}) => {
  // Render node with improved visual hierarchy
  const renderNode = (node: Node, level: number = 0) => {
    if (node.type === 'scope') {
      return (
        <div key={node.id} data-node-id={node.id} className="node-animate w-full bg-white overflow-hidden border-2 border-emerald-200 shadow-sm hover:shadow-md transition-all">
          {/* Scope Header */}
          <div
            className="px-8 py-6 bg-emerald-600 transition-all flex justify-between items-center group cursor-pointer"
            onClick={() => selectNode(node)}
          >
            <div className="flex items-center gap-4 flex-1">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" strokeWidth="2"/>
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-white/80 text-sm font-medium uppercase tracking-wide">Compliance Domain</div>
                <input
                  type="text"
                  placeholder="e.g., Information Security, Data Privacy, Quality Management"
                  value={node.name}
                  readOnly
                  className="bg-transparent text-white font-bold text-xl outline-none w-full placeholder-white/60"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-white/80 text-sm">
                {node.children?.length || 0} sections
              </div>
              {templateStatus === 'draft' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteNode(node.id);
                  }}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center text-white transition-colors opacity-0 group-hover:opacity-100"
                  title="Delete Domain"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeWidth="2"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
          {/* Scope Content */}
          <div className="p-8 bg-slate-50">
            {/* Scope Description */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-sm font-semibold text-slate-700">Domain Objective</span>
              </div>
              <div
                className="bg-white border border-slate-200 rounded-xl p-4 hover:border-emerald-300 transition-colors cursor-pointer"
                onClick={() => selectNode(node)}
              >
                <input
                  type="text"
                  placeholder="Describe what this compliance domain covers..."
                  value={node.description || ''}
                  readOnly
                  className="font-medium text-slate-800 bg-transparent outline-none w-full placeholder-slate-400"
                />
              </div>
            </div>

            {/* Sections Container */}
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" strokeWidth="2"/>
                </svg>
                <span className="text-sm font-semibold text-slate-700">Compliance Sections</span>
                <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                  {node.children?.filter(c => c.type === 'section').length || 0} sections
                </span>
              </div>

              <div className="ml-6 relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-emerald-200"></div>
                <div className="space-y-4">
                  {node.children && node.children.filter(c => c.type === 'section').map(child => renderNode(child, level + 1))}
                  {templateStatus === 'draft' && (
                    <div
                      className="placeholder border-2 border-dashed border-slate-300 rounded-xl py-8 flex flex-col items-center justify-center text-slate-500 bg-slate-50/50 hover:bg-emerald-50/50 hover:border-emerald-300 transition-all group overflow-hidden"
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (draggedType === 'section') {
                          e.currentTarget.classList.add('drag-over');
                        }
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove('drag-over');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('drag-over');
                        if (draggedType === 'section') {
                          handleZoneDrop('section', node.id)(draggedType);
                        }
                        setDraggedType(null);
                      }}
                    >
                      <div className="text-center">
                        <svg className="w-8 h-8 text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <p className="text-sm font-medium text-slate-600 mb-1">
                          {node.children && node.children.filter(c => c.type === 'section').length > 0
                            ? 'Add Another Compliance Section'
                            : 'Add Your First Compliance Section'}
                        </p>
                        <p className="text-xs text-slate-500">Drag a section from the toolbar to organize your requirements</p>
                      </div>

                      {/* Tooltip */}
                      {activeTooltip === 'drop-section' && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg whitespace-nowrap z-50">
                          Drag a section from the toolbar to add it here
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-800"></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (node.type === 'section') {
      return (
        <div key={node.id} data-node-id={node.id} className="relative node-animate w-full bg-white border border-blue-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
          {/* Section Header */}
          <div
            className="px-6 py-4 flex justify-between items-center cursor-pointer bg-slate-50 transition-colors border-b border-blue-200/30"
            onClick={() => selectNode(node)}
          >
            <div className="flex items-center gap-3 flex-1">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeWidth="2"/>
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-blue-600/80 text-xs font-medium uppercase tracking-wide">Compliance Section</div>
                <input
                  type="text"
                  placeholder="e.g., Access Control, Risk Assessment, Incident Response"
                  value={node.name}
                  readOnly
                  className="bg-transparent text-slate-800 font-bold text-lg outline-none w-full"
                />
              </div>
            </div>
            {templateStatus === 'draft' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteNode(node.id);
                }}
                className="text-slate-500 hover:text-red-400 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeWidth="2"/>
                </svg>
              </button>
            )}
          </div>
          <div className="p-6 space-y-4 bg-slate-50">
            <div className="relative ml-4 mb-2 hover:bg-slate-50 transition-colors">
              <input
                type="text"
                placeholder="Section description..."
                value={node.description || ''}
                readOnly
                onClick={() => selectNode(node)}
                className="font-medium text-slate-700 bg-transparent outline-none w-full"
              />
            </div>
            <div className="border-t border-slate-300 my-4 pb-4"></div>
            <div className="ml-6 relative dropzone req-container pb-4 bg-transparent rounded-xl">
              <div className="tree-branch"></div>
              <div className="pl-4 space-y-4">
                {node.children && node.children.map(child => renderNode(child, level + 1))}
                {templateStatus === 'draft' && (
                  <div
                    className="placeholder border-2 border-dashed border-slate-200/50 rounded-2xl py-6 flex flex-col items-center justify-center text-slate-400 bg-slate-50/30 hover:from-emerald-50/30 hover:to-emerald-100/20 transition-all"
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (draggedType === 'requirement') {
                        e.currentTarget.classList.add('drag-over');
                      }
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('drag-over');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('drag-over');
                      if (draggedType === 'requirement') {
                        handleZoneDrop('requirement', node.id)(draggedType);
                      }
                      setDraggedType(null);
                    }}
                  >
                    <p className="text-[10px] font-bold uppercase tracking-widest">+ Drop Requirement Here</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    } else if (node.type === 'requirement') {
      const nodeFieldsList = nodeFields.get(node.id) || [];
      console.log(`Renderer: node.id=${node.id}, nodeFieldsList=`, nodeFieldsList);

      return (
        <div key={node.id} className="relative group node-animate mt-6">
          <div className="tree-connector"></div>
          <div
            data-node-id={node.id}
            onClick={() => selectNode(node)}
            className={`bg-white border p-6 rounded-[1.5rem] flex gap-5 hover:border-emerald-500 transition-all cursor-pointer relative active:ring-2 ring-indigo-200 ${
              selectedNode?.id === node.id ? 'border-indigo-500 ring-2 ring-indigo-100' :
              (node as any).needs_review ? 'border-amber-300' :
              'border-slate-200'
            }`}
          >
            {(node as any).needs_review ? (
              <div
                className="absolute -top-2 -right-2 bg-amber-400 text-amber-900 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1"
                title={(node as any).review_reason || 'AI-defaulted value — review before publishing.'}
              >
                <span>⚠</span>
                Needs review
              </div>
            ) : (node as any).architect_generated ? (
              <div
                className="absolute -top-2 -right-2 bg-indigo-100 text-indigo-700 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm"
                title="Produced by AI Framework Architect"
              >
                AI expert
              </div>
            ) : null}
            <div className="w-10 h-10 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center shrink-0 font-bold text-xs shadow-sm">REQ</div>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Title"
                value={node.name}
                readOnly
                onClick={() => selectNode(node)}
                className="block w-full font-bold text-slate-800 bg-transparent outline-none"
              />
              <input
                type="text"
                placeholder="Description..."
                value={node.description || ''}
                readOnly
                onClick={() => selectNode(node)}
                className="block w-full text-[11px] text-slate-400 bg-transparent outline-none mt-1"
              />
            </div>
            {templateStatus === 'draft' && (
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleDuplicateNode(node)}
                className="text-slate-300 hover:text-indigo-500 transition-colors"
                title="Duplicate"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" strokeWidth="2"/>
                </svg>
              </button>
              <button
                onClick={() => deleteNode(node.id)}
                className="text-slate-300 hover:text-red-500 transition-colors"
                title="Delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" strokeWidth="2"/>
                </svg>
              </button>
            </div>
            )}
          </div>

          {/* Fields Drop Zone */}
          <div
            className={`ml-16 mt-4 relative field-dropzone min-h-[60px] rounded-xl ${
              templateStatus === 'draft'
                ? 'bg-transparent'
                : 'bg-slate-50/30'
            }`}
            {...(templateStatus === 'draft' ? {
              onDragOver: (e: React.DragEvent) => {
                e.preventDefault();
                if (draggedFieldType) {
                  e.currentTarget.classList.add('drag-over');
                }
              },
              onDragLeave: (e: React.DragEvent) => {
                e.currentTarget.classList.remove('drag-over');
              },
              onDrop: (e: React.DragEvent) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
                if (draggedFieldType) {
                  handleFieldDrop(node.id, draggedFieldType);
                }
                setDraggedFieldType(null);
              }
            } : {})}
          >
            <div className="tree-branch"></div>
            <div className="pl-4 py-2">
              {/* Render existing fields */}
              {nodeFieldsList.length > 0 && (
                <div className="space-y-2">
                  {nodeFieldsList.map((field, index) => {
                    const getFieldColorClass = (fieldType: string) => {
                      switch (fieldType) {
                        case 'text': return 'bg-blue-100 border-blue-300 text-blue-800';
                        case 'number': return 'bg-emerald-100 border-emerald-300 text-emerald-800';
                        case 'date': return 'bg-purple-100 border-purple-300 text-purple-800';
                        case 'boolean': return 'bg-amber-100 border-amber-300 text-amber-800';
                        case 'document': return 'bg-rose-100 border-rose-300 text-rose-800';
                        case 'calculated': return 'bg-cyan-100 border-cyan-300 text-cyan-800';
                        case 'select': return 'bg-violet-100 border-violet-300 text-violet-800';
                        case 'multiselect': return 'bg-violet-100 border-violet-300 text-violet-800';
                        case 'textarea': return 'bg-teal-100 border-teal-300 text-teal-800';
                        default: return 'bg-slate-100 border-slate-300 text-slate-800';
                      }
                    };

                    return (
                    <div
                      key={field.id}
                      draggable
                      onDragStart={() => {
                        setDraggedField(field);
                        setDragOverFieldId(null);
                        setDragOverPosition(null);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (draggedField && draggedField.id !== field.id) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const y = e.clientY - rect.top;
                          const height = rect.height;
                          const position = y < height / 2 ? 'before' : 'after';
                          setDragOverFieldId(field.id);
                          setDragOverPosition(position);
                        }
                      }}
                      onDragLeave={() => {
                        if (dragOverFieldId === field.id) {
                          setDragOverFieldId(null);
                          setDragOverPosition(null);
                        }
                      }}
                      onDrop={() => {
                        if (draggedField && draggedField.id !== field.id) {
                          reorderFields(draggedField, field);
                        }
                        setDraggedField(null);
                        setDragOverFieldId(null);
                        setDragOverPosition(null);
                      }}
                      onClick={() => selectField(field)}
                      className={`relative py-2 px-4 flex items-center justify-between group/field hover:from-white hover:to-white hover:border-slate-300 transition-all cursor-pointer border rounded-lg shadow-sm ${getFieldColorClass(field.field_type)} ${
                        dragOverFieldId === field.id && dragOverPosition === 'before' ? 'field-drag-over-before' : ''
                      } ${
                        dragOverFieldId === field.id && dragOverPosition === 'after' ? 'field-drag-over-after' : ''
                      }`}
                    >
                      <div className="field-connector"></div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold">{index + 1}. {field.field_name}</span>
                        <span className="text-[9px] bg-white/50 px-2 py-0.5 rounded uppercase backdrop-blur-sm">{field.field_type}</span>
                        {field.is_required ? (
                          <span className="text-[9px] text-red-500 font-bold">*</span>
                        ) : (
                          <span className="text-[9px] text-slate-400 font-bold">○</span>
                        )}
                      </div>
                      {templateStatus === 'draft' && (
                      <div className="flex gap-2 opacity-0 group-hover/field:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateField(field, node.id);
                          }}
                          className="text-slate-300 hover:text-indigo-500 transition-colors"
                          title="Duplicate"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" strokeWidth="2"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const realNodeId = mockToReal.current.get(node.id) || parseInt(node.id);
                            if (!isNaN(realNodeId)) {
                              deleteField(realNodeId, field.id);
                            }
                          }}
                          className="text-slate-300 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" strokeWidth="2"/>
                          </svg>
                        </button>
                      </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}

              {/* Drop field placeholder - shown below existing fields */}
              {templateStatus === 'draft' && (
                <p className="text-[9px] font-bold text-indigo-400 uppercase tracking-widest mt-2">+ Drop Field Here</p>
              )}
            </div>
          </div>

          {/* Sub-requirements drop zone */}
          <div
            className="ml-16 mt-4 relative sub-dropzone min-h-[40px]"
            onDragOver={(e) => {
              e.preventDefault();
              if (draggedType === 'sub_requirement') {
                e.currentTarget.classList.add('drag-over');
              }
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove('drag-over');
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('drag-over');
              if (draggedType === 'sub_requirement') {
                handleZoneDrop('sub_requirement', node.id)(draggedType);
              }
              setDraggedType(null);
            }}
          >
            <div className="tree-branch"></div>
            <div className="pl-4 space-y-4">
              {node.children && node.children.map(child => renderNode(child, level + 1))}
              {templateStatus === 'draft' && (
              <div className="sub-placeholder border border-dashed border-slate-200/50 rounded-xl py-2 flex justify-center text-[9px] font-bold text-slate-300 uppercase tracking-widest transition-all hover:from-white hover:to-slate-50 bg-slate-50/30">+ Drop Sub-Req</div>
              )}
            </div>
          </div>
        </div>
      );
    } else if (node.type === 'sub_requirement') {
      const nodeFieldsList = nodeFields.get(node.id) || [];
      console.log(`Renderer: node.id=${node.id}, nodeFieldsList=`, nodeFieldsList);

      return (
        <div key={node.id} className="relative node-animate">
          <div className="tree-connector"></div>
          <div data-node-id={node.id} className="bg-slate-50/50 border border-slate-200/50 py-3 px-5 rounded-xl flex items-center justify-between group/sub hover:from-white hover:to-slate-50 hover:border-amber-400 transition-all cursor-pointer" onClick={() => selectNode(node)}>
            <input
              type="text"
              placeholder="Sub-task..."
              value={node.name}
              readOnly
              onClick={() => selectNode(node)}
              className="text-[11px] font-bold text-slate-600 italic bg-transparent outline-none w-full"
            />
            {templateStatus === 'draft' && (
            <div className="flex gap-2 opacity-0 group-hover/sub:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDuplicateNode(node);
                }}
                className="text-slate-300 hover:text-indigo-500 transition-colors"
                title="Duplicate"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" strokeWidth="2"/>
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteNode(node.id);
                }}
                className="text-slate-300 hover:text-red-500 transition-colors"
                title="Delete"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" strokeWidth="2"/>
                </svg>
              </button>
            </div>
            )}
          </div>

          {/* Fields Drop Zone for Sub-Requirement */}
          <div
            className="ml-4 mt-2 relative field-dropzone min-h-[60px] bg-transparent rounded-xl"
            onDragOver={(e) => {
              e.preventDefault();
              if (draggedFieldType) {
                e.currentTarget.classList.add('drag-over');
              }
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove('drag-over');
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('drag-over');
              if (draggedFieldType) {
                handleFieldDrop(node.id, draggedFieldType);
              }
              setDraggedFieldType(null);
            }}
          >
            <div className="tree-branch"></div>
            <div className="pl-4 py-2">
              {templateStatus === 'draft' && (
              <p className="text-[9px] font-bold text-indigo-400 uppercase tracking-widest mb-2">+ Drop Field Here</p>
              )}

              {/* Render existing fields */}
              {nodeFieldsList.length > 0 && (
                <div className="space-y-2">
                  {nodeFieldsList.map((field, index) => {
                    const getFieldColorClass = (fieldType: string) => {
                      switch (fieldType) {
                        case 'text': return 'bg-blue-100 border-blue-300 text-blue-800';
                        case 'number': return 'bg-emerald-100 border-emerald-300 text-emerald-800';
                        case 'date': return 'bg-purple-100 border-purple-300 text-purple-800';
                        case 'boolean': return 'bg-amber-100 border-amber-300 text-amber-800';
                        case 'document': return 'bg-rose-100 border-rose-300 text-rose-800';
                        case 'calculated': return 'bg-cyan-100 border-cyan-300 text-cyan-800';
                        case 'select': return 'bg-violet-100 border-violet-300 text-violet-800';
                        case 'multiselect': return 'bg-violet-100 border-violet-300 text-violet-800';
                        case 'textarea': return 'bg-teal-100 border-teal-300 text-teal-800';
                        default: return 'bg-slate-100 border-slate-300 text-slate-800';
                      }
                    };

                    return (
                    <div
                      key={field.id}
                      draggable
                      onDragStart={() => {
                        setDraggedField(field);
                        setDragOverFieldId(null);
                        setDragOverPosition(null);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (draggedField && draggedField.id !== field.id) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const y = e.clientY - rect.top;
                          const height = rect.height;
                          const position = y < height / 2 ? 'before' : 'after';
                          setDragOverFieldId(field.id);
                          setDragOverPosition(position);
                        }
                      }}
                      onDragLeave={() => {
                        if (dragOverFieldId === field.id) {
                          setDragOverFieldId(null);
                          setDragOverPosition(null);
                        }
                      }}
                      onDrop={() => {
                        if (draggedField && draggedField.id !== field.id) {
                          reorderFields(draggedField, field);
                        }
                        setDraggedField(null);
                        setDragOverFieldId(null);
                        setDragOverPosition(null);
                      }}
                      onClick={() => selectField(field)}
                      className={`relative py-2 px-4 flex items-center justify-between group/field hover:from-white hover:to-white hover:border-slate-300 transition-all cursor-pointer border rounded-lg shadow-sm ${getFieldColorClass(field.field_type)} ${
                        dragOverFieldId === field.id && dragOverPosition === 'before' ? 'field-drag-over-before' : ''
                      } ${
                        dragOverFieldId === field.id && dragOverPosition === 'after' ? 'field-drag-over-after' : ''
                      }`}
                    >
                      <div className="field-connector"></div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold">{index + 1}. {field.field_name}</span>
                        <span className="text-[9px] bg-white/50 px-2 py-0.5 rounded uppercase backdrop-blur-sm">{field.field_type}</span>
                        {field.is_required ? (
                          <span className="text-[9px] text-red-500 font-bold">*</span>
                        ) : (
                          <span className="text-[9px] text-slate-400 font-bold">○</span>
                        )}
                      </div>
                      {templateStatus === 'draft' && (
                      <div className="flex gap-2 opacity-0 group-hover/field:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateField(field, node.id);
                          }}
                          className="text-slate-300 hover:text-indigo-500 transition-colors"
                          title="Duplicate"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" strokeWidth="2"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const realNodeId = mockToReal.current.get(node.id) || parseInt(node.id);
                            if (!isNaN(realNodeId)) {
                              deleteField(realNodeId, field.id);
                            }
                          }}
                          className="text-slate-300 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" strokeWidth="2"/>
                          </svg>
                        </button>
                      </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {nodes.map(node => renderNode(node))}
    </div>
  );
};

export default ComplianceStructureBuilderRenderer;
